import {
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  Search2Icon,
  SearchIcon,
} from "@chakra-ui/icons";
import { Box, Button, Center, Flex, Grid, Text } from "@chakra-ui/react";
import apiDigitacao from "api/api-digitacao";
import { dropdownConvenioAtendimento } from "components/atendimentos-components/atendimento-form/fields-data";
import { dropdownBancosSimulacaoFGTS } from "components/atendimentos-components/pagina-atendimento/pagined-form/parts/simulacoes/utils";
import { Checkbox } from "components/checkbox";
import { DropdownField } from "components/dropdown-field";
import {
  CustomTableColor,
  CustomTableColorColumn,
} from "components/dynamic-table/color-table";
import {
  bgError,
  bgSucess,
  StatusFormat,
} from "components/dynamic-table/row-status";
import { GetLayout } from "components/get-layout";
import { InputBoxSearch } from "components/input-box-search";
import { InputField } from "components/input-field";
import { openModalConfirm } from "components/modal-confirm-new";
import { Toast } from "components/toast";
import { useFilterEmpresa } from "components/usuarios-bancos-components/utils";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useEffect, useState } from "react";
import { filterArrayBy } from "utils/filter-array-by";
import { isUserGerente } from "utils/is-gerente";

interface Filter {
  convenio: string;
  tipoOperacao: string;
  banco: string;
  customerId: number | undefined;
}

interface Tabela {
  banco: string;
  codigo: string;
  convenio: string;
  descricao: string;
  enabledCustomer: boolean;
  enabledPromotora: boolean;
  id: number;
  prazo: null;
  tipoOperacao: null;
  availableBanco?: boolean;
  suspensaPromotora?: boolean;
  dentroValidade?: boolean;
}

export function TabelasDigitacaoPage() {
  let [filter, setFilter] = useState<Partial<Filter>>({});
  const [searchValue, setSearchValue] = useState("");
  const [somenteHabilitadas, setSomenteHabilitadas] = useState(false);
  const [tabelasList, setTabelasList] = useState<Tabela[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingRow, setLoadingRow] = useState<number>();
  const { user } = useApplicationContext();
  let { empresas, loadingEmpresas, onChangeEmpresa, selectedEmpresa } =
    useFilterEmpresa({
      onChange(customerId) {
        selectedEmpresa = customerId;
        onChangeFilter("customerId", customerId);
      },
    });

  const isGerente = isUserGerente(user);

  const bancos = (
    {
      INSS: INSSBancosList.sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", {
          sensitivity: "base",
        })
      ),
      ANTECIPACAO_FGTS: dropdownBancosSimulacaoFGTS.sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", {
          sensitivity: "base",
        })
      ),
      CONSIGNADO_TRABALHADOR: [{ name: "Facta", value: "FACTA" }],
      standard: dropdownBancosSimulacaoFGTS.sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", {
          sensitivity: "base",
        })
      ),
    } as any
  )[filter.convenio ?? "standard"];

  const filteredTabelasList = filterArrayBy(
    tabelasList,
    searchValue,
    ["descricao"],
    { partialSearch: true }
  ).filter(
    ({
      enabledPromotora,
      availableBanco,
      suspensaPromotora,
      enabledCustomer,
    }) => {
      if (somenteHabilitadas) {
        if (
          !(!availableBanco || !enabledPromotora || suspensaPromotora) &&
          enabledCustomer
        ) {
          return true;
        } else return false;
      } else return true;
    }
  );

  const isDisabled = isGerente
    ? !selectedEmpresa || !filter.convenio || !filter.banco
    : !filter.convenio || !filter.banco;

  const onChangeFilter = (k: keyof Filter, value: any) => {
    filter = { ...filter, [k]: value };
    setFilter(filter);
    setTabelasList([]);
    setSearchValue("");
  };

  const getResults = async ({
    withOutLoading = false,
    currFilter = filter,
  } = {}) => {
    const params = {
      convenio: currFilter.convenio ?? "",
      banco: currFilter.banco ?? "",
      customer_id: currFilter.customerId,
    };

    if (!withOutLoading) setIsLoading(true);
    try {
      const { data } = await apiDigitacao.get(`/api/tabelas-digitacao`, {
        params,
      });
      setTabelasList(data);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message;
      Toast({
        title: errorMessage ?? "Erro ao obter resultados",
        status: "error",
      });
    } finally {
      if (!withOutLoading) setIsLoading(false);
    }
  };

  const handleEnableOrDisable = async ({
    id,
    enabledCustomer,
    enabledPromotora,
    availableBanco,
  }: Tabela) => {
    openModalConfirm({
      onConfirm: async () => {
        const isRemove = enabledPromotora === false || availableBanco === false;
        setLoadingRow(id);
        try {
          const { data } = await apiDigitacao.patch(
            isRemove
              ? `/api/tabelas-digitacao/${id}/disable`
              : enabledCustomer
              ? `/api/tabelas-digitacao/${id}/disable`
              : `/api/tabelas-digitacao/${id}/enable`,
            null,
            { params: { customer_id: filter.customerId } }
          );
          await getResults({ withOutLoading: true });
        } catch (e) {
          Toast({
            title: `Erro ao ${
              isRemove
                ? "remover"
                : enabledCustomer
                ? "desabilitar"
                : "habilitar"
            }`,
            status: "error",
          });
        } finally {
          setLoadingRow(undefined);
        }
      },
    });
  };

  const checkEnabledOrDisabled = (row: Tabela) => {
    const enabledPromotora = row.enabledPromotora;
    const availableBanco = row.availableBanco;
    const suspensaPromotora = row.suspensaPromotora;
    const dentroValidade = row.dentroValidade;
    let color = bgError;
    let status = "Desabilitado";

    if (!availableBanco) {
      status = "Indisponível Banco";
    } else if (dentroValidade === false) {
      status = "Finalizada pela Promotora";
    } else if (!enabledPromotora) {
      status = "Desabilitada pela Promotora";
    } else if (suspensaPromotora) {
      status = "Suspensa pela Promotora";
    } else if (row.enabledCustomer) {
      status = "Habilitado";
      color = bgSucess;
    }
    return { status, color };
  };

  const columns: CustomTableColorColumn<Tabela>[] = [
    {
      name: "Código",
      render: (row) => {
        return row.codigo;
      },
    },
    { name: "Descrição", render: (row) => row.descricao },
    {
      name: "Convênio",
      render: (row) =>
        dropdownConvenioAtendimento.find((crr) => crr.value === row.convenio)
          ?.name,
    },
    {
      name: "Banco",
      render: (row) =>
        parseBancos.find(({ value }) => value === row.banco)?.name ?? row.banco,
    },
    {
      name: "Status",
      headerStyles: { textAlign: "center" },
      render: (row) => {
        let buttonDesc = "Habilitar";
        let icon = <CheckIcon />;
        const isRemove =
          row.enabledPromotora === false ||
          row.availableBanco === false ||
          row.suspensaPromotora === true ||
          row.dentroValidade === false;

        if (isRemove) {
          buttonDesc = "Remover";
          icon = <DeleteIcon />;
        } else if (row.enabledCustomer) {
          buttonDesc = "Desabilitar";
          icon = <CloseIcon w="10px" h="10px" />;
        }

        return (
          <>
            <Center gap="10px">
              <StatusFormat minW="100px" bg={checkEnabledOrDisabled(row).color}>
                {checkEnabledOrDisabled(row).status}
              </StatusFormat>
              <Button
                w="103px"
                leftIcon={icon}
                size="xs"
                variant="outline"
                onClick={() => handleEnableOrDisable(row)}
                isLoading={loadingRow === row.id}
                loadingText={"Carregando"}
              >
                {buttonDesc}
              </Button>
            </Center>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (!isDisabled) {
      getResults();
    }
  }, [filter]);

  useEffect(() => {
    if (isGerente) onChangeEmpresa(user.userData.customerId);
  }, []);

  return (
    <GetLayout>
      <Flex alignItems="center" justifyContent="space-between" mb="30px">
        <Text fontSize="25" fontWeight="bold">
          Tabelas Digitação
        </Text>
      </Flex>
      {isGerente &&
      user.userData.customerChildrenInheritTabelasDigitacao !== true ? (
        <Grid templateColumns="1fr 1fr 1fr 179px 1fr" gap="12px">
          <Box w="100%" gridArea="auto / span 2" mb="16px" mr="8px">
            <Text mb="8px" fontWeight="medium">
              Empresa
            </Text>
            <InputBoxSearch
              options={empresas}
              onChange={onChangeEmpresa}
              checkIsInclude={(option) => {
                return selectedEmpresa === option.value;
              }}
              isLoading={loadingEmpresas}
              value={
                empresas.find((customer) => selectedEmpresa === customer.value)
                  ?.name
              }
              inputBoxProps={{
                bg: "#fff",
                onFocus: (e) => {
                  e.currentTarget.textContent = "";
                },
                onBlur: (e) => {
                  e.currentTarget.textContent =
                    empresas.find(
                      (customer) => selectedEmpresa === customer.value
                    )?.name || "";
                },
              }}
              openOnFocus
              closeOnSelect
            />
          </Box>
        </Grid>
      ) : null}
      <Grid
        templateColumns={"1fr 1fr 1fr 179px 1fr"}
        w="100%"
        gap="12px"
        mb="12px"
      >
        <Box>
          <DropdownField
            title="Convênio"
            onChange={(value) => {
              onChangeFilter("convenio", value);
              onChangeFilter("banco", undefined);
            }}
            value={filter.convenio}
            dropdownProps={{ w: "100%", bg: "#fff" }}
            options={conveniosList}
          />
        </Box>
        <Box>
          <DropdownField
            title="Banco"
            onChange={(value) => onChangeFilter("banco", value)}
            value={filter.banco}
            dropdownProps={{ w: "100%", bg: "#fff" }}
            options={bancos}
            isDisabled={!filter.convenio}
          />
        </Box>
        <Box>
          <InputField
            title="Pesquisar tabelas"
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            value={searchValue}
            rightIcon={<SearchIcon />}
            inputProps={{
              bg: "#fff",
              _placeholder: { opacity: 0.8 },
            }}
            isDisabled={tabelasList.length === 0}
          />
        </Box>

        <Box>
          <Text mb="8px">&nbsp;</Text>
          <Flex h="32px" alignItems="center">
            <Checkbox
              isChecked={somenteHabilitadas}
              onChange={setSomenteHabilitadas}
            >
              Somente Habilitadas
            </Checkbox>
          </Flex>
        </Box>
        <Box>
          <Text mb="8px">&nbsp;</Text>
          <Button
            w="100%"
            leftIcon={<Search2Icon />}
            onClick={() => getResults()}
            isLoading={isLoading}
            isDisabled={isDisabled}
            loadingText="Carregando Tabelas"
          >
            Pesquisar
          </Button>
        </Box>
      </Grid>

      <CustomTableColor rows={filteredTabelasList} columns={columns} />
    </GetLayout>
  );
}

const INSSBancosList = [
  { name: "Facta", value: "FACTA" },
  { name: "PAN", value: "PAN" },
  { name: "C6", value: "C6" },
  { name: "Bmg", value: "BMG" },
  { name: "iCred", value: "ICRED" },
  { name: "Master", value: "MASTER" },
  { name: "Presença", value: "PRESENCA" },
  { name: "Daycoval", value: "DAYCOVAL" },
];

const tipoOperacaoAntecipacaoFGTSArray = [
  { name: "Antecipação FGTS", value: "ANTECIPACAO_FGTS" },
];

const conveniosList = [
  { name: "INSS", value: "INSS" },
  { name: "Antecipação FGTS", value: "ANTECIPACAO_FGTS" },
  { name: "Consignado CLT", value: "CONSIGNADO_TRABALHADOR" },
];

const parseBancos = [
  { name: "Facta", value: "FACTA" },
  { name: "Facta PINE", value: "FACTA_PINE" },
  { name: "PAN", value: "PAN" },
  { name: "Safra", value: "SAFRA" },
  { name: "Master", value: "MASTER" },
  { name: "Itaú", value: "ITAU" },
  { name: "Bmg", value: "BMG" },
  { name: "Novo Saque", value: "NOVO_SAQUE" },
  { name: "Lotus", value: "LOTUS_TAYA" },
  { name: "UY3", value: "UY3" },
  { name: "VCTeX", value: "VCTEX" },
  { name: "Tá Quitado", value: "TA_QUITADO" },
  { name: "Tá Quitado BMP", value: "TA_QUITADO_BMP" },
  { name: "Tá Quitado ZIPDIN", value: "TA_QUITADO_ZIPDIN" },
  { name: "X3 BMP", value: "X3_BMP" },
  { name: "V8", value: "V8" },
  { name: "V8 BMS", value: "V8_BMS" },
  { name: "V8 QITECH", value: "V8_QITECH" },
  { name: "V8 CARTOS", value: "V8_CARTOS" },
  { name: "iCred", value: "ICRED" },
  { name: "Daycoval", value: "DAYCOVAL" },
  { name: "C6", value: "C6" },
  { name: "2S Consig", value: "TWO_S_CONSIG" },
];
